import { computeCascadeOffset, MIN_MARGIN } from './cascade';
import { clamp } from './clamp';

/** The screen area available for placing a popup (CSSOM screen metrics). */
export interface ScreenAvail {
    /** Left edge of the available area, in screen pixels. */
    availLeft: number;
    /** Top edge of the available area, in screen pixels. */
    availTop: number;
    /** Width of the available area, in screen pixels. */
    availWidth: number;
    /** Height of the available area, in screen pixels. */
    availHeight: number;
}

/** Position and size for a popup window, in screen pixels. */
export interface PopupBounds {
    /** Left edge of the window. */
    left: number;
    /** Top edge of the window. */
    top: number;
    /** Window width. */
    width: number;
    /** Window height. */
    height: number;
}

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;

/**
 * Computes where to place the next popup so that multiple windows cascade
 * instead of stacking exactly, always staying within the available screen.
 *
 * @example
 * ```ts
 * const bounds = computeNextNativeBounds(0, {
 *     availLeft: 0,
 *     availTop: 0,
 *     availWidth: 1920,
 *     availHeight: 1080,
 * });
 * ```
 */
export function computeNextNativeBounds(
    openCount: number,
    screen: ScreenAvail,
): PopupBounds {
    const width = Math.min(
        DEFAULT_WIDTH,
        Math.max(MIN_MARGIN, screen.availWidth),
    );
    const height = Math.min(
        DEFAULT_HEIGHT,
        Math.max(MIN_MARGIN, screen.availHeight),
    );

    const baseLeft =
        screen.availLeft + Math.floor((screen.availWidth - width) / 2);
    const baseTop =
        screen.availTop + Math.floor((screen.availHeight - height) / 2);

    const cascadeOffset = computeCascadeOffset(
        openCount,
        screen.availWidth,
        screen.availHeight,
        width,
        height,
    );

    const rawLeft = baseLeft + cascadeOffset;
    const rawTop = baseTop + cascadeOffset;

    const maxLeft = screen.availLeft + screen.availWidth - width;
    const maxTop = screen.availTop + screen.availHeight - height;

    return {
        left: clamp(rawLeft, screen.availLeft, maxLeft),
        top: clamp(rawTop, screen.availTop, maxTop),
        width,
        height,
    };
}

/**
 * Builds the `window.open` features string for the next native popup, cascading
 * its placement within the available screen via {@link computeNextNativeBounds}.
 *
 * @example
 * ```ts
 * window.open(url, name, nativePopupFeatures(openCount));
 * ```
 */
export function nativePopupFeatures(openCount: number): string {
    // `availLeft` / `availTop` are part of the CSSOM Window screen interface
    // but are missing from the TS lib types in this project.
    const screen = window.screen as Screen & {
        availLeft: number;
        availTop: number;
    };
    const bounds = computeNextNativeBounds(openCount, {
        availLeft: screen.availLeft,
        availTop: screen.availTop,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
    });
    return [
        'popup=yes',
        `width=${bounds.width}`,
        `height=${bounds.height}`,
        `left=${bounds.left}`,
        `top=${bounds.top}`,
    ].join(',');
}
